import React from 'react'
import { getHexColor } from '@/atoms/utils'
import { NamedColor } from '@/constants/types'
import { useTranslate } from '@/utils/translate/translate-client'
import { HlsTooltip } from './HlsTooltip'

/** Chromecast icon — from Bitmovin skin-modern/images/chromecast.svg (32x32 viewBox). */
const ChromecastIcon: React.FC<{ color: NamedColor }> = ({ color }) => {
  const hex = getHexColor(color)

  return (
    <svg width={24} height={24} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill={hex}
        d="M27.7 5.4H6.4c-1.2 0-2.1 1-2.1 2.1v2.1h2.1V7.5h21.3v12.8h-8.5v2.1h8.5c1.2 0 2.1-1 2.1-2.1V7.5c0-1.2-.9-2.1-2.1-2.1z"
      />
      <path
        fill={hex}
        d="M2.2 12.8v2.1c6.5 0 11.7 5.2 11.7 11.7H16C16 19 9.8 12.8 2.2 12.8zM2.2 18.1v2.1c3.5 0 6.4 2.9 6.4 6.4h2.1c0-4.6-3.8-8.5-8.5-8.5zM2.2 23.4v3.2h3.2c0-1.8-1.4-3.2-3.2-3.2z"
      />
    </svg>
  )
}

type CastState = 'unavailable' | 'available' | 'connecting' | 'connected'

interface HlsCastButtonProps {
  castState: CastState
  onStartCast: () => void
  onStopCast: () => void
}

/**
 * Google Cast (Chromecast) toggle button for the control bar.
 * Only renders when Cast devices are available on the network.
 * Toggles between starting and stopping a cast session.
 */
export const HlsCastButton: React.FC<HlsCastButtonProps> = ({ castState, onStartCast, onStopCast }) => {
  const { t } = useTranslate('common')

  if (castState === 'unavailable') return null

  const isCasting = castState === 'connected' || castState === 'connecting'

  const handleClick = () => {
    if (isCasting) {
      onStopCast()
    } else {
      onStartCast()
    }
  }

  return (
    <button
      type="button"
      className="group relative flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20"
      onClick={handleClick}
      aria-label={t('googleCast', 'Google Cast')}
    >
      <ChromecastIcon color={isCasting ? 'oxide-bright' : 'white'} />
      <HlsTooltip
        title={t('googleCast', 'Google Cast')}
        subtitle={
          isCasting
            ? t('googleCastDisconnect', 'Stop casting to TV.')
            : t('googleCastConnect', 'Cast to a nearby device.')
        }
      />
    </button>
  )
}
